import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  TextField,
  Button,
  Typography,
  Tooltip,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import RedditIcon from '@mui/icons-material/Reddit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import PinterestIcon from '@mui/icons-material/Pinterest';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SmsIcon from '@mui/icons-material/Sms';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import { 
  Share as ShareIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Cloud as CloudIcon
} from '@mui/icons-material';
import ShareGoogleDriveModal from './ShareGoogleDriveModal';
import { API_BASE_URL } from '../config/api';
import TwitchIcon from './TwitchIcon';
import DiscordIcon from './DiscordIcon';

interface Message {
  _id: string;
  userId: string;
  content: string;
  type: 'user' | 'ai';
  createdAt: string;
  aiResponse?: {
    image?: string;
  };
}

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  responseText?: string;
  responseImage?: string;
  onTextChange: (text: string) => void;
  onShare: (platform: string, images?: string[]) => void;
  conversationMessages?: Message[];
  conversationImages?: string[];
}

const platforms = [
  { 
    name: 'Facebook', 
    icon: <FacebookIcon color="primary" />,
    color: '#1877F2',
    description: 'Share on Facebook'
  },
  { 
    name: 'X', 
    icon: <TwitterIcon sx={{ color: '#000' }} />,
    color: '#000000',
    description: 'Share on X (Twitter)'
  },
  { 
    name: 'Reddit', 
    icon: <RedditIcon sx={{ color: '#FF4500' }} />,
    color: '#FF4500',
    description: 'Share on Reddit'
  },
  { 
    name: 'WhatsApp', 
    icon: <WhatsAppIcon sx={{ color: '#25D366' }} />,
    color: '#25D366',
    description: 'Share via WhatsApp'
  },
  { 
    name: 'Pinterest', 
    icon: <PinterestIcon sx={{ color: '#E60023' }} />,
    color: '#E60023',
    description: 'Share on Pinterest'
  },
  { 
    name: 'LinkedIn', 
    icon: <LinkedInIcon color="primary" />,
    color: '#0A66C2',
    description: 'Share on LinkedIn'
  },
  { 
    name: 'SMS', 
    icon: <SmsIcon color="primary" />,
    color: '#4CAF50',
    description: 'Share via SMS'
  },
  { 
    name: 'Google Drive', 
    icon: <CloudQueueIcon sx={{ color: '#4285F4' }} />,
    color: '#4285F4',
    description: 'Save to Google Drive'
  },
  { 
    name: 'Gmail', 
    icon: <EmailIcon sx={{ color: '#EA4335' }} />,
    color: '#EA4335',
    description: 'Share via Gmail'
  },
  { 
    name: 'Instagram', 
    icon: <InstagramIcon sx={{ color: '#E4405F' }} />,
    color: '#E4405F',
    description: 'Share on Instagram'
  },
  {
    name: 'Twitch',
    icon: <TwitchIcon />,
    color: '#9147FF',
    description: 'Share on Twitch (copy to clipboard)'
  },
  {
    name: 'Discord',
    icon: <DiscordIcon />,
    color: '#5865F2',
    description: 'Share on Discord (copy to clipboard)'
  },
];

const ShareModal: React.FC<ShareModalProps> = ({ open, onClose, responseText = '', responseImage, onTextChange, onShare, conversationMessages, conversationImages }) => {
  const [googleDriveOpen, setGoogleDriveOpen] = React.useState(false);
  const [isGoogleDriveConnected, setIsGoogleDriveConnected] = React.useState<boolean>(false);
  const [checkingDrive, setCheckingDrive] = React.useState<boolean>(true);
  const [showMainModal, setShowMainModal] = React.useState(true);

  // Callback to hide both modals when Google Picker opens
  const handleHideModals = () => {
    setShowMainModal(false);
  };

  // Callback to show both modals when Google Picker closes
  const handleShowModals = () => {
    setShowMainModal(true);
  };

  React.useEffect(() => {
    if (open) {
      setCheckingDrive(true);
      fetch(`${API_BASE_URL}/google-drive/token`, { credentials: 'include' })
        .then(res => setIsGoogleDriveConnected(res.ok))
        .catch(() => setIsGoogleDriveConnected(false))
        .finally(() => setCheckingDrive(false));
    }
  }, [open]);

  React.useEffect(() => {
    function handleMessage(event: MessageEvent) {
      console.log('Received postMessage:', event.data);
      if (event.data && event.data.type === 'GOOGLE_DRIVE_CONNECTED') {
        setCheckingDrive(true);
        fetch(`${API_BASE_URL}/google-drive/token`, { credentials: 'include' })
          .then(res => {
            console.log('Token endpoint status after postMessage:', res.status);
            setIsGoogleDriveConnected(res.ok);
          })
          .catch(() => setIsGoogleDriveConnected(false))
          .finally(() => setCheckingDrive(false));
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleConnectGoogleDrive = async () => {
    const res = await fetch(`${API_BASE_URL}/google-drive/auth/initiate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    const data = await res.json();
    if (data.authUrl) {
      const win = window.open(data.authUrl, '_blank');
      // Start polling for connection status
      let elapsed = 0;
      const pollInterval = 1000; // 1 second
      const maxTime = 60000; // 60 seconds
      setCheckingDrive(true);
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/google-drive/token`, { credentials: 'include' });
          if (response.ok) {
            clearInterval(interval);
            setIsGoogleDriveConnected(true);
            setCheckingDrive(false);
            if (win && !win.closed) win.close();
          }
        } catch (error) {
          // Ignore, keep polling
        }
        elapsed += pollInterval;
        if (elapsed >= maxTime) {
          clearInterval(interval);
          setCheckingDrive(false);
        }
      }, pollInterval);
    }
  };

  const handlePlatformShare = (platform: string) => {
    console.log('Clicked platform:', platform, 'isGoogleDriveConnected:', isGoogleDriveConnected);
    if (platform === 'Google Drive') {
      if (!isGoogleDriveConnected) {
        console.warn('Google Drive button clicked but not connected!');
        // Optionally, you could show a toast or message here
        return;
      }
      setGoogleDriveOpen(true);
    } else {
      onShare(platform);
    }
  };

  // Format conversation messages into a single text block for editing/sharing
  const formatConversation = (messages: Message[]) => {
    return messages.map(msg => {
      const sender = msg.type === 'user' ? 'User' : 'AI';
      const date = new Date(msg.createdAt).toLocaleString();
      let text = `**${sender}** (${date}):\n${msg.content}`;
      if (msg.aiResponse?.image) {
        text += `\n[Image attached: ${msg.aiResponse.image}]`;
      }
      return text;
    }).join('\n\n');
  };

  // Use conversation text if provided, else fallback to responseText
  const [conversationText, setConversationText] = React.useState('');
  React.useEffect(() => {
    if (conversationMessages && conversationMessages.length > 0) {
      setConversationText(formatConversation(conversationMessages));
    } else {
      setConversationText(responseText);
    }
  }, [conversationMessages, responseText]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setConversationText(e.target.value);
    onTextChange(e.target.value);
  };

  // Extract all image URLs from conversationMessages if provided
  const images = React.useMemo(() => {
    if (conversationImages && conversationImages.length > 0) return conversationImages;
    if (conversationMessages && conversationMessages.length > 0) {
      return conversationMessages
        .map(msg => msg.aiResponse?.image)
        .filter((img): img is string => !!img);
    }
    return responseImage ? [responseImage] : [];
  }, [conversationMessages, conversationImages, responseImage]);

  return (
    <>
      <Dialog 
        open={open && showMainModal} 
        onClose={onClose} 
        aria-labelledby="share-dialog-title" 
        fullWidth 
        maxWidth="md"
        sx={{
          '& .MuiBackdrop-root': {
            display: showMainModal ? 'block' : 'none'
          },
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle 
          id="share-dialog-title" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pb: 1,
            '& .MuiTypography-root': {
              fontWeight: 600,
              fontSize: '1.25rem'
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <ShareIcon sx={{ color: 'primary.main' }} />
            Share AI Response
          </Box>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ px: 3, py: 2 }}>
          {/* Preview Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon sx={{ fontSize: 20 }} />
              Preview & Edit
            </Typography>
            {/* Show all images if present */}
            {images.length > 0 && (
              <Box sx={{ mb: 2, textAlign: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                {images.map((img, idx) => (
                  <Paper elevation={2} sx={{ p: 1, display: 'inline-block' }} key={img + idx}>
                    <img 
                      src={img} 
                      alt={`Conversation image ${idx + 1}`} 
                      style={{ 
                        maxWidth: 180, 
                        maxHeight: 120, 
                        borderRadius: 8,
                        display: 'block',
                        margin: '0 auto'
                      }} 
                    />
                  </Paper>
                ))}
              </Box>
            )}
            <TextField
              multiline
              minRows={4}
              maxRows={16}
              fullWidth
              value={conversationText}
              onChange={handleTextChange}
              variant="outlined"
              aria-label="Edit share text"
              placeholder="Edit your conversation before sharing..."
              sx={{ 
                '& .MuiInputBase-root': {
                  bgcolor: 'transparent'
                }
              }}
            />
          </Box>

          {/* Google Drive Connection Status */}
          {!checkingDrive && !isGoogleDriveConnected && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={handleConnectGoogleDrive}
                  startIcon={<CloudIcon />}
                  sx={{ 
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Connect
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Google Drive not connected
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Connect your account to save responses to Google Drive
              </Typography>
            </Alert>
          )}

          {checkingDrive && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Checking Google Drive connection...
              </Typography>
            </Box>
          )}

          {/* Share Platforms */}
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Share to Platform
            </Typography>
            
            {/* Social Media */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                Social Media
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="center">
                {platforms.filter(p => ['X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(p.name)).map((platform) => (
                  <Tooltip title={platform.description} key={platform.name}>
                    <span>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'pointer' : 'default',
                          border: '2px solid transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? platform.color : 'transparent',
                            transform: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'translateY(-2px)' : 'none',
                            boxShadow: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 3 : 2
                          },
                          opacity: (platform.name === 'Google Drive' && !isGoogleDriveConnected) || 
                                  !['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 0.5 : 1
                        }}
                        onClick={() => {
                          if (platform.name === 'Twitch' || platform.name === 'Discord') {
                            navigator.clipboard.writeText(conversationText).then(() => {
                              alert(`Copied to clipboard! Paste into your ${platform.name} post.`);
                            });
                          } else if (platform.name === 'X') {
                            let text = conversationText;
                            if (images.length > 0) {
                              text = `📸 Image: ${images[0]}\n\n${text}`;
                            }
                            const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
                            window.open(twitterUrl, '_blank');
                          } else if (platform.name === 'LinkedIn') {
                            let text = conversationText;
                            if (images.length > 0) {
                              text = `📸 Image: ${images[0]}\n\n${text}`;
                            }
                            const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent('AI Response')}&summary=${encodeURIComponent(text)}`;
                            window.open(linkedinUrl, '_blank');
                          } else if (platform.name === 'WhatsApp') {
                            let text = conversationText;
                            if (images.length > 0) {
                              text = `🖼️ Image available at: ${images[0]}\n\n${text}`;
                            }
                            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
                            window.open(whatsappUrl, '_blank');
                          } else if (platform.name === 'Instagram') {
                            const text = (images.length > 0 ? `📸 Image: ${images[0]}\n\n` : '') + conversationText;
                            alert(`Instagram sharing:\n\n${text}\n\nPlease copy this content and create your Instagram post manually.`);
                          } else if (platform.name === 'Facebook') {
                            let text = conversationText;
                            if (images.length > 0) {
                              text = `📸 Image: ${images[0]}\n\n${text}`;
                            }
                            alert(`Facebook sharing:\n\n${text}\n\nPlease copy this content and paste it into your Facebook post.`);
                            const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`;
                            window.open(facebookUrl, '_blank');
                          } else if (platform.name === 'Reddit') {
                            let text = conversationText;
                            if (images.length > 0) {
                              text = `📸 Image: ${images[0]}\n\n${text}`;
                            }
                            const redditUrl = `https://reddit.com/submit?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent('AI Response')}&text=${encodeURIComponent(text)}`;
                            window.open(redditUrl, '_blank');
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {platform.icon}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {platform.name}
                          </Typography>
                        </Box>
                      </Paper>
                    </span>
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* Direct Sharing - SMS & Email */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                Direct Sharing
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="center">
                {platforms.filter(p => ['SMS', 'Gmail'].includes(p.name)).map((platform) => (
                  <Tooltip title={platform.description} key={platform.name}>
                    <span>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'pointer' : 'default',
                          border: '2px solid transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? platform.color : 'transparent',
                            transform: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'translateY(-2px)' : 'none',
                            boxShadow: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 3 : 2
                          },
                          opacity: (platform.name === 'Google Drive' && !isGoogleDriveConnected) || 
                                  !['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 0.5 : 1
                        }}
                        onClick={() => {
                          if (platform.name === 'SMS') {
                            window.open(`sms:?body=${encodeURIComponent((images.length > 0 ? 'Image: ' + images[0] + '\n\n' : '') + conversationText)}`);
                          } else if (platform.name === 'Gmail') {
                            const subject = 'AI Response';
                            const body = (images.length > 0 ? 'Image: ' + images[0] + '\n\n' : '') + conversationText;
                            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&tf=1&to=&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                            window.open(gmailUrl, '_blank');
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {platform.icon}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {platform.name}
                          </Typography>
                        </Box>
                      </Paper>
                    </span>
                  </Tooltip>
                ))}
              </Stack>
            </Box>

            {/* Cloud Storage */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
                Cloud Storage
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap justifyContent="center">
                {platforms.filter(p => ['Google Drive'].includes(p.name)).map((platform) => (
                  <Tooltip title={platform.description} key={platform.name}>
                    <span>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          cursor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'pointer' : 'default',
                          border: '2px solid transparent',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? platform.color : 'transparent',
                            transform: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 'translateY(-2px)' : 'none',
                            boxShadow: ['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 3 : 2
                          },
                          opacity: (platform.name === 'Google Drive' && !isGoogleDriveConnected) || 
                                  !['SMS', 'Google Drive', 'Gmail', 'X', 'LinkedIn', 'WhatsApp', 'Instagram', 'Facebook', 'Reddit', 'Twitch', 'Discord'].includes(platform.name) ? 0.5 : 1
                        }}
                        onClick={() => {
                          if (platform.name === 'Google Drive') {
                            onShare(platform.name, images);
                          }
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1.5}>
                          {platform.icon}
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {platform.name}
                          </Typography>
                        </Box>
                      </Paper>
                    </span>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={onClose} 
            color="secondary"
            sx={{ 
              fontWeight: 500,
              textTransform: 'none'
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <ShareGoogleDriveModal
        open={googleDriveOpen}
        onClose={() => setGoogleDriveOpen(false)}
        responseText={responseText}
        responseImage={responseImage}
        onHideModals={handleHideModals}
        onShowModals={handleShowModals}
        showModal={showMainModal}
      />
    </>
  );
};

export default ShareModal; 